import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link, useParams } from "react-router-dom";
import { Row, Col, Card, Button, Badge, Alert, Spinner, Modal, Table } from "react-bootstrap";
import Swal from "sweetalert2";
import axios from "axios";
import PageLayout from "../../layouts/PageLayout";
import "suneditor/dist/css/suneditor.min.css";

const normalizeProductAttributes = (attributes) => {
    let parsedAttributes = attributes;

    if (typeof parsedAttributes === "string") {
        try {
            parsedAttributes = JSON.parse(parsedAttributes);
        } catch {
            return [];
        }
    }

    const entries = Array.isArray(parsedAttributes)
        ? parsedAttributes.map((attribute) => [attribute?.name, attribute?.value])
        : parsedAttributes && typeof parsedAttributes === "object"
            ? Object.entries(parsedAttributes).map(([name, value]) => [
                value && typeof value === "object" ? value.name ?? name : name,
                value && typeof value === "object" ? value.value : value,
            ])
            : [];

    return entries
        .filter(([name]) => name !== null && name !== undefined && String(name).trim())
        .map(([name, value]) => ({
            name: String(name),
            value: value === null || value === undefined || value === ""
                ? "—"
                : typeof value === "object"
                    ? value.name || value.title || "—"
                    : String(value),
        }));
};

const cleanHtmlContent = (html) => {
    if (typeof html !== "string" || !html.trim()) return "";

    const unescaped = html
        .replace(/\\"/g, '"')
        .replace(/\\\//g, "/")
        .replace(/\\r\\n|\\n|\\t/g, "");
    const parser = new DOMParser();
    let document = parser.parseFromString(unescaped, "text/html");

    if (!document.body.children.length && /<\/?[a-z][^>]*>/i.test(document.body.textContent)) {
        document = parser.parseFromString(document.body.textContent, "text/html");
    }

    document.body.querySelectorAll("script, style, iframe, object, embed").forEach((element) => element.remove());
    document.body.querySelectorAll("*").forEach((element) => {
        Array.from(element.attributes).forEach((attribute) => {
            const name = attribute.name.toLowerCase();
            const value = attribute.value.trim().toLowerCase();
            if (
                name.startsWith("on") ||
                ((name === "href" || name === "src" || name === "xlink:href") &&
                    /^(javascript|data):/.test(value))
            ) {
                element.removeAttribute(attribute.name);
            }
        });
    });

    return document.body.innerHTML;
};

const STATUS_VARIANT = {
    live: "success",
    inactive: "dark",
    sold: "primary",
    pending: "warning",
    draft: "secondary",
    rejected: "danger",
    awaiting_shipment: "info",
    shipped: "info",
    received: "info",
    checking: "info",
};

const money = (value) =>
    value === null || value === undefined || value === "" ? "—" : `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = (value) => (value ? new Date(value).toLocaleString() : "—");
const text = (value) => (value === null || value === undefined || value === "" ? "—" : String(value));

function InfoGrid({ rows }) {
    return (
        <Row className="g-3">
            {rows.filter(Boolean).map(([label, value]) => (
                <Col sm={6} lg={4} key={label}>
                    <div className="small text-muted">{label}</div>
                    <div className="fw-semibold">{value}</div>
                </Col>
            ))}
        </Row>
    );
}

export default function SellerProductDetailPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = useParams();
    const productId = id || location.state?.item?.id;

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [previewImage, setPreviewImage] = useState("");
    const [busy, setBusy] = useState(false);

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("AuthUser") || ""}`,
    });

    // Always load the full product (list pages only have a short summary).
    useEffect(() => {
        if (!productId) {
            setLoading(false);
            return;
        }
        let active = true;
        (async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/store-seller/product/${productId}`, {
                    headers: authHeaders(),
                });
                if (active) setProduct(response.data?.data || null);
            } catch (requestError) {
                if (active) setError(requestError.response?.data?.message || "Could not load product details.");
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [productId]);

    const toggleActive = async (activate) => {
        const confirm = await Swal.fire({
            icon: "question",
            title: activate ? "Make this product live again?" : "Make this product inactive?",
            text: activate
                ? "It will be shown on the website again."
                : "It will be hidden from the website and removed from buyers' carts. You can make it live again any time.",
            showCancelButton: true,
            confirmButtonText: activate ? "Make live" : "Make inactive",
        });
        if (!confirm.isConfirmed) return;
        setBusy(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/store-seller/product/${product.id}/active`,
                { active: activate },
                { headers: authHeaders() }
            );
            const reload = await axios.get(`${process.env.REACT_APP_API_URL}/store-seller/product/${product.id}`, { headers: authHeaders() });
            setProduct(reload.data?.data || product);
            Swal.fire({ icon: "success", text: response.data?.message, timer: 2000, showConfirmButton: false });
        } catch (requestError) {
            Swal.fire({ icon: "error", text: requestError.response?.data?.message || "Could not update the product." });
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <PageLayout><div className="text-center py-5"><Spinner animation="border" /></div></PageLayout>;
    }
    if (!product) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    {error && <Alert variant="danger">{error}</Alert>}
                    <h5>No product details available.</h5>
                    <Button variant="primary" className="mt-3" onClick={() => navigate(-1)}>Back to list</Button>
                </div>
            </PageLayout>
        );
    }

    const imgBaseUrl = process.env.REACT_APP_IMG_URL || "";
    const img = (path) => (!path ? "" : /^https?:\/\//i.test(path) ? path : `${imgBaseUrl}${path}`);
    const productAttributes = normalizeProductAttributes(product.attributes);
    const category = [product.category_parent, product.category?.name || product.category].filter(Boolean).join(" › ");
    const gallery = [product.card_img, ...(Array.isArray(product.images) ? product.images.map((i) => i?.image) : [])].filter(Boolean);

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h3 className="mc-breadcrumb-title">Product Details</h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item"><Link to="/seller-products?tab=all">Products</Link></li>
                                    <li className="mc-breadcrumb-item">{product.product_code || `#${product.id}`}</li>
                                </ul>
                            </div>
                            <div className="d-flex gap-2 flex-wrap">
                                {product.is_live && (
                                    <Button variant="outline-dark" disabled={busy} onClick={() => toggleActive(false)}>Make inactive</Button>
                                )}
                                {product.is_inactive && (
                                    <Button variant="success" disabled={busy} onClick={() => toggleActive(true)}>Make live again</Button>
                                )}
                                {product.can_edit && (
                                    <Button as={Link} to="/seller-product-edit" state={{ item: product }} variant="primary">Edit</Button>
                                )}
                                <Button variant="secondary" onClick={() => navigate(-1)}>Back</Button>
                            </div>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    {product.status_key === "rejected" && (
                        <Alert variant="danger">
                            <strong>This product was rejected.</strong>
                            {product.status_reason ? <div className="mt-1">Reason: {product.status_reason}</div> : null}
                        </Alert>
                    )}
                    {product.is_inactive && (
                        <Alert variant="secondary">This product is inactive: it is hidden from the website and cannot be bought.</Alert>
                    )}
                    {product.status_key === "awaiting_shipment" && (
                        <Alert variant="info">
                            Approved. Ship this watch to the Watches Pool store from <Link to="/seller-store-shipments/create">Ship Products</Link>.
                        </Alert>
                    )}
                </Col>

                {/* Left: photos */}
                <Col lg={4}>
                    <Card className="mb-4 border-0 shadow-sm">
                        <Card.Body className="text-center">
                            {product.card_img && (
                                <img
                                    src={img(product.card_img)}
                                    alt={product.title || "Product"}
                                    className="img-fluid rounded mb-3"
                                    style={{ maxHeight: 280, objectFit: "contain", cursor: "zoom-in" }}
                                    onClick={() => setPreviewImage(img(product.card_img))}
                                />
                            )}
                            <h5 className="mb-2">{product.title}</h5>
                            <div className="d-flex justify-content-center gap-2 flex-wrap">
                                {product.product_code && <Badge bg="primary">{product.product_code}</Badge>}
                                <Badge bg={STATUS_VARIANT[product.status_key] || "secondary"}>{product.status}</Badge>
                            </div>
                        </Card.Body>
                    </Card>

                    {gallery.length > 1 && (
                        <Card className="mb-4 border-0 shadow-sm">
                            <Card.Header className="bg-white"><h6 className="mb-0">Photos ({gallery.length})</h6></Card.Header>
                            <Card.Body>
                                <Row className="g-2">
                                    {gallery.map((path) => (
                                        <Col key={path} xs={4}>
                                            <img
                                                src={img(path)}
                                                alt="Product"
                                                className="img-fluid rounded border"
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => setPreviewImage(img(path))}
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter" || event.key === " ") {
                                                        event.preventDefault();
                                                        setPreviewImage(img(path));
                                                    }
                                                }}
                                                style={{ height: 80, width: "100%", objectFit: "cover", cursor: "zoom-in" }}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </Col>

                {/* Right: details */}
                <Col lg={8}>
                    <Card className="mb-4 border-0 shadow-sm">
                        <Card.Header className="bg-white"><h5 className="mb-0">Product information</h5></Card.Header>
                        <Card.Body>
                            <InfoGrid
                                rows={[
                                    ["Title", text(product.title)],
                                    ["Product code", text(product.product_code)],
                                    ["Category", text(category)],
                                    ["Brand", text(product.brand?.name || product.brand)],
                                    ["Store", text(product.store?.name || product.store)],
                                    ["Condition", text(product.product_condition)],
                                    ["Selling style", text(product.selling_style)],
                                    ["Quantity", text(product.product_qty)],
                                    ["Views", text(product.click_count)],
                                    ["Listed on", when(product.created_at)],
                                    ["Last updated", when(product.updated_at)],
                                ]}
                            />
                        </Card.Body>
                    </Card>

                    <Card className="mb-4 border-0 shadow-sm">
                        <Card.Header className="bg-white"><h5 className="mb-0">Pricing</h5></Card.Header>
                        <Card.Body>
                            <InfoGrid
                                rows={[
                                    ["Sell price", money(product.sell_price_dollar)],
                                    ["Shipping charges", money(product.shipping_charges_dollar)],
                                    product.selling_style === "Auction" ? ["Auction minimum price", money(product.auction_min_price)] : null,
                                    ["Buying price", product.buying_price ? text(product.buying_price) : "—"],
                                    ["Short buying price", text(product.short_buying_price)],
                                ]}
                            />
                        </Card.Body>
                    </Card>

                    {productAttributes.length > 0 && (
                        <Card className="mb-4 border-0 shadow-sm">
                            <Card.Header className="bg-white"><h5 className="mb-0">Item specifics</h5></Card.Header>
                            <Card.Body>
                                <Table size="sm" bordered className="align-middle mb-0">
                                    <tbody>
                                        {productAttributes.map((attribute, index) => (
                                            <tr key={`${attribute.name}-${index}`}>
                                                <th scope="row" className="w-50">{attribute.name}</th>
                                                <td>{attribute.value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    )}

                    {(product.short_description || product.description) && (
                        <Card className="mb-4 border-0 shadow-sm">
                            <Card.Header className="bg-white"><h5 className="mb-0">Descriptions</h5></Card.Header>
                            <Card.Body>
                                {product.short_description && (
                                    <>
                                        <div className="small text-muted mb-1">Short description</div>
                                        <div className="sun-editor-editable mb-3 p-0" dangerouslySetInnerHTML={{ __html: cleanHtmlContent(product.short_description) }} />
                                    </>
                                )}
                                {product.description && (
                                    <>
                                        <div className="small text-muted mb-1">Description</div>
                                        <div className="sun-editor-editable p-0" dangerouslySetInnerHTML={{ __html: cleanHtmlContent(product.description) }} />
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {product.order && (
                        <Card className="mb-4 border-0 shadow-sm">
                            <Card.Header className="bg-white"><h5 className="mb-0">Order</h5></Card.Header>
                            <Card.Body>
                                <InfoGrid
                                    rows={[
                                        ["Order", text(product.order.order_number)],
                                        ["Ordered", when(product.order.ordered_at)],
                                        ["Buyer", text([product.order.buyer_name, product.order.buyer_country].filter(Boolean).join(", "))],
                                        ["Sold for", money(product.order.unit_price)],
                                        ["Payment", text(product.order.payment_status)],
                                        ["Delivery", text(product.order.delivery_status)],
                                        ["Courier", text(product.order.courier_company)],
                                        ["Tracking", text(product.order.tracking_number)],
                                    ]}
                                />
                            </Card.Body>
                        </Card>
                    )}

                    {product.payout && (
                        <Card className="mb-4 border-0 shadow-sm">
                            <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Your payout</h5>
                                <Link to="/seller-payouts" className="small">All payouts</Link>
                            </Card.Header>
                            <Card.Body>
                                <InfoGrid
                                    rows={[
                                        ["Sale price", money(product.payout.gross_usd)],
                                        [`Platform fee (${Number(product.payout.fee_percent)}%)`, money(product.payout.fee_usd)],
                                        ["Deductions", money(Number(product.payout.shipping_deduction_usd || 0) + Number(product.payout.other_deductions_usd || 0))],
                                        ["You receive", money(product.payout.net_usd)],
                                        ["Status", text(product.payout.status_label)],
                                        ["Held until", text(product.payout.hold_until)],
                                    ]}
                                />
                                {product.payout.hold_reason && <p className="small text-muted mt-3 mb-0">{product.payout.hold_reason}</p>}
                            </Card.Body>
                        </Card>
                    )}

                    {Array.isArray(product.timeline) && product.timeline.length > 0 && (
                        <Card className="mb-4 border-0 shadow-sm">
                            <Card.Header className="bg-white"><h5 className="mb-0">History</h5></Card.Header>
                            <Card.Body>
                                <Table size="sm" responsive className="align-middle mb-0">
                                    <thead><tr><th>Date</th><th>Step</th><th>Status</th><th>Note</th></tr></thead>
                                    <tbody>
                                        {[...product.timeline].reverse().map((entry) => (
                                            <tr key={entry.id}>
                                                <td className="text-nowrap">{when(entry.date)}</td>
                                                <td>{text(entry.type)}</td>
                                                <td>{[entry.status, entry.status_2].filter(Boolean).join(" / ") || "—"}</td>
                                                <td>{text(entry.reason)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    )}
                </Col>
            </Row>

            <Modal show={Boolean(previewImage)} onHide={() => setPreviewImage("")} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Product photo</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center p-2">
                    {previewImage && (
                        <img src={previewImage} alt={product.title || "Product"} className="img-fluid" style={{ maxHeight: "75vh", objectFit: "contain" }} />
                    )}
                </Modal.Body>
            </Modal>
        </PageLayout>
    );
}
